/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

// ALERTS LIST
import { PageQueryParams } from '../apiCommon.typings';

export interface AlertsListDto {
    count: number;
    alerts: AlertDto[];
}

export interface AlertsSearchParams {
    limit: number;
    offset: number;
    sort: AlertsSortType;
    status?: AlertStatus[];
    priority?: AlertPriority[];
    type?: AlertName[];
    assigneeUserId?: AlertUserDto['id'][];
    dateFrom?: number;
}

export const ALERTS_SORT_TYPES = [
    'inserted,desc',
    'inserted,asc',
    'type.priority,desc',
    'type.priority,asc',
    'id,desc',
    'id,asc',
    'itemsCount,desc',
    'itemsCount,asc',
    'assigneeUserId,desc',
    'assigneeUserId,asc',
    'status,desc',
    'status,asc',
] as const;

export type AlertsSortType = (typeof ALERTS_SORT_TYPES)[number];

export interface AlertDto {
    id: number;
    type: AlertTypeDto;
    status: AlertStatus;
    itemsCount: number;
    inserted?: number;
    assigneeUser: AlertUserDto;
}

export interface AlertTypeDto {
    name: AlertName;
    entityType: EntityType;
    priority: AlertPriority;
}

export const ALERT_NAME = [
    'CASH_BOX_WITH_INVALID_TIME_ALERT',
    'CASH_BOX_CERTIFICATE_IS_EXPIRED_ALERT',
    'INVALID_DOCUMENT_REFERENCE_ALERT',
    'CREATION_DATE_IS_AFTER_THAN_INSERTION_DATE_ALERT',
    'MISSED_STORE_INFORMATION_ALERT',
    'MISSED_COMPANY_INFORMATION_ALERT',
    'CASH_BOX_WITH_FAILED_AUDIT_ALERT',
    'CASH_BOX_WITHOUT_AUDIT_ALERT',
    'DOCUMENTS_CHAIN_GAP_ALERT',
    'WRONG_VAT_PERCENT_ALERT',
    'INACTIVE_STORE_WITH_ACTIVE_CASH_BOX_ALERT',
    'ACTIVE_STORE_WHERE_ALL_CASH_BOXES_BLOCKED_ALERT',
    'ACTIVE_TAXPAYER_WITH_NO_ACTIVE_CASH_BOX_ALERT',
    'CLOSED_TAXPAYER_WITH_ACTIVE_CASH_BOX_ALERT',
    'ACTIVE_CASH_BOX_WITHOUT_DOCS_ALERT',
    'CASHBOX_WITH_INVALID_GPS_ALERT',
    'STORE_WITH_INVALID_GPS_ALERT',
] as const;

export type AlertName = (typeof ALERT_NAME)[number];

export const ENTITY_TYPES = ['CASH_BOX', 'DOCUMENT', 'COMPANY', 'STORE'] as const;

export type EntityType = (typeof ENTITY_TYPES)[number];

export const ALERT_PRIORITIES = ['MINOR', 'NORMAL', 'MAJOR'] as const;
export type AlertPriority = (typeof ALERT_PRIORITIES)[number];

export const ALERT_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as const;

export type AlertStatus = (typeof ALERT_STATUSES)[number];

export interface AlertUserDto {
    id: number;
    firstName: string;
    lastName: string;
}

// ALERT ITEMS LIST
export interface AlertItemsListDto {
    count: number;
    items: AlertItemDto[];
}

export interface AlertItemsSearchParams {
    limit: number;
    offset: number;
}

export type AlertItemDto = AlertCashBoxItemDto | AlertCompanyItemDto | AlertDocumentItemDto | AlertStoreItemDto;

interface AlertItemBaseDto {
    id: number;
    alertId: AlertDto['id'];
    ignore: boolean;
    ignoreReason: string;
}

export interface AlertCashBoxItemDto extends AlertItemBaseDto {
    content: {
        cashBoxTaxNumber: string;
        storeTaxNumber: string;
        storeName: string;
        storeType: string;
        extended: Record<string, string>;
    };
}

export interface AlertDocumentItemDto extends AlertItemBaseDto {
    content: {
        docId: string;
        storeTaxNumber: string;
        storeName: string;
        storeType: string;
        createdAtUtc: number;
        extended: Record<string, string>;
    };
}

export interface AlertCompanyItemDto extends AlertItemBaseDto {
    content: {
        companyTaxNumber: string;
        companyName: string;
        extended: Record<string, string>;
    };
}

export interface AlertStoreItemDto extends AlertItemBaseDto {
    content: {
        storeTaxNumber: string;
        storeName: string;
        storeType: string;
        extended: Record<string, string>;
    };
}

// ALERTS TEMPLATES

export interface AlertTemplateDTO {
    description: string;
    enabled: boolean;
    group: AlertTemplateGroup;
    id: number;
    key: AlertName;
    misfiredTriggerPolicy?: number;
    parameters?: TemplateParameters;
    schedule: string;
    period: Period | null;
    assigneeUser: AlertUserDto;
    alertEntityType: EntityType;
}

export const ALERT_TEMPLATE_GROUPS = ['period', 'status'] as const;

export type AlertTemplateGroup = (typeof ALERT_TEMPLATE_GROUPS)[number];

export const PERIODS = ['PREVIOUS_DAY', 'PREVIOUS_WEEK', 'PREVIOUS_MONTH'] as const;

export type Period = (typeof PERIODS)[number];

export interface TemplateParameters {
    start: string;
    end: string;
}

export interface TemplatesList {
    count: number;
    alertsTemplates: AlertTemplateDTO[];
}

export const TEMPLATES_SORT_TYPES = ['id,desc', 'id,asc'] as const;

export type TemplatesListSortType = (typeof TEMPLATES_SORT_TYPES)[number];

export interface TemplatesListSearchParams {
    limit: number;
    offset: number;
    sort?: TemplatesListSortType;
}

export interface TemplateExecuteParameters {
    start: string;
    end: string;
}

// ALERTS EXECUTIONS

export const TEMPLATES_EXECUTION_STATES = ['SCHEDULED', 'RUNNING', 'FAILED', 'SUCCEED'] as const;

export type TemplateExecutionState = (typeof TEMPLATES_EXECUTION_STATES)[number];

export interface TemplateExecutionDTO {
    duration: string;
    id: number;
    parameters: TemplateParameters;
    regular: boolean;
    retriesCount: number;
    started: string;
    state: TemplateExecutionState;
}

export interface TemplateExecutionListDTO {
    alertsChecksExecutions: TemplateExecutionDTO[];
    count: number;
}

export const SORT_ORDERS = ['desc', 'asc'] as const;

export type SortOrder = (typeof SORT_ORDERS)[number];

export const TEMPLATES_EXECUTIONS_SORT_TYPES = [
    'started,desc',
    'started,asc',
    'regular,desc',
    'regular,asc',
    'retriesCount,desc',
    'retriesCount,asc',
    'duration,asc',
    'duration,desc',
    'state,desc',
    'state,asc',
] as const;

export type TemplateExecutionsSortType = (typeof TEMPLATES_EXECUTIONS_SORT_TYPES)[number];

export interface TemplateExecutionsSearchParams {
    limit: number;
    offset: number;
    sort: TemplateExecutionsSortType;
}

export enum RuleType {
    DENY_LIST = 'DENYLIST',
    ALLOW_LIST = 'ALLOWLIST',
}

export enum TemplateRuleEntityType {
    DEFAULT = 'DEFAULT',
    COMPANY = 'TAXPAYER',
    STORE = 'OBJECT',
    CASH_BOX = 'CASH_BOX',
    CASHREGISTER_MODEL = 'CASHREGISTER_MODEL',
}

export interface CreateTemplateRuleDto {
    ruleType: RuleType;
    ruleEntityType: TemplateRuleEntityType;
    taxNumber: string;
}
export interface TemplateRuleTaxNumbersSearchParams extends Omit<CreateTemplateRuleDto, 'ruleType'> {}

export interface CreateTemplateRulesParams {
    clear: boolean;
    rules: CreateTemplateRuleDto[];
}

export interface TemplateRuleDto extends CreateTemplateRuleDto {
    id: number;
    insertedAtUtc: string;
}

export interface TemplateRulesListSearchParams extends Partial<PageQueryParams> {
    taxNumber: string;
    ruleEntityType: TemplateRuleEntityType;
    ruleType: RuleType;
}

export interface TemplateRulesListDto {
    count: number;
    rules: TemplateRuleDto[];
}

export interface UpdateTemplateRuleDto {
    ruleType: RuleType;
}
